import { iConnectorContactSideBarData } from "@sdk/chat-connectors/chat-connectors.models";
import { useBetaFeatures } from "components/pages/conversations/use-beta-features";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { loadAllChatConnectors } from "store/modules/chat-connectors/chat-connectors.helpers";
import { selectAllChatConnectors } from "store/modules/chat-connectors/chat-connectors.selectors";
import {
  selectConversationCustomProperties,
  selectIntegrationConfig_AITA,
  selectIntegrationConfig_TADesk,
  selectIntegrationConfigs,
  selectIsAgentQCEnabled,
  selectMailchimpIntegrationConfig,
  selectShopifyStoreConfig,
  selectTicketTypes,
} from "store/modules/workspace/workspace.selectors";
import { useQueryWithStore } from "store/store.hooks";
import { IconRender } from "../../manage-chat-connectors/icon-selector";

const _sectionHeadings = [
  {
    section: "PROFILE",
    title: "Customer Profile",
    icon: "ri-shield-user-line",
  },
  {
    section: "RECENT_CONVERSATIONS",
    title: "Recent Conversations",
    icon: "ri-chat-history-line",
  },
  {
    section: "RECENT_ACTIVITIES",
    title: "Recent Activities",
    icon: "ri-walk-fill",
  },
  {
    section: "SCHEDULED_MESSAGES",
    title: "Scheduled Messages",
    icon: "ri-calendar-todo-line",
  },
];

export const useChatBarAvailableSections = () => {
  const customProperties = useSelector(selectConversationCustomProperties);
  const isAgentQCEnabled = useSelector(selectIsAgentQCEnabled);
  const shopifyStoreConfig = useSelector(selectShopifyStoreConfig);
  const mailchimpIntegrationConfig = useSelector(
    selectMailchimpIntegrationConfig,
  );

  const { state: chatConnectors, retry: reload } = useQueryWithStore(
    selectAllChatConnectors,
    loadAllChatConnectors(),
    [],
  );
  const chatConnectorSidebars = useMemo(
    () =>
      chatConnectors.filter(
        (connector) => connector.type === "CONTACT_SIDE_BAR",
      ),
    [chatConnectors],
  );

  const betaFeatures = useBetaFeatures();
  const ticketTypes = useSelector(selectTicketTypes);
  const integrationConfigs = useSelector(selectIntegrationConfigs);
  const aitaConfig = useSelector(selectIntegrationConfig_AITA);
  const isAitaEnabled = !!aitaConfig && !!aitaConfig?.config?.enabled;
  const taDeskConfig = useSelector(selectIntegrationConfig_TADesk);
  const isTaDeskEnabled = !!taDeskConfig && !!taDeskConfig?.config?.enabled;

  const sectionHeadings = useMemo(
    () => [
      ..._sectionHeadings,
      ...(shopifyStoreConfig?.isActive
        ? [
            {
              section: "SHOPIFY_STORE",
              title: "Shopify Store",
              icon: "ri-store-line",
            },
          ]
        : []),
      ...(mailchimpIntegrationConfig?.isActive
        ? [
            {
              section: "MAIL_CHIMP",
              title: "Mailchimp",
              icon: () => (
                <img
                  src="/assets/integrations/mailchimp.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(customProperties.length > 0
        ? [
            {
              section: "CUSTOM_PROPERTIES",
              title: "Custom Properties",
              icon: "ri-profile-line",
            },
          ]
        : []),
      ...(window.location.href.includes("localhost")
        ? [
            {
              section: "PIPELINE_STAGE_CHECK_LIST",
              title: "Check Lists",
              icon: "ri-checkbox-multiple-line",
            },
            {
              section: "LINKED_CARDS",
              title: "Linked Cards",
              icon: "ri-layout-top-2-line",
            },
            {
              section: "OPPORTUNITIES",
              title: "Recent Opportunities",
              icon: "ri-money-dollar-box-fill",
            },
            {
              section: "EVENTS",
              title: "Recent Events",
              icon: "ri-calendar-line",
            },
          ]
        : []),
      ...(ticketTypes?.length > 0
        ? [
            {
              section: "TICKETS",
              title: "Linked Trackers",
              icon: "ri-folder-open-line",
            },
          ]
        : []),
      ...(isAgentQCEnabled
        ? [
            {
              section: "AGENT_QC",
              title: "Agent QC",
              icon: "ri-shield-check-line",
            },
          ]
        : []),
      ...(integrationConfigs?.stripe
        ? [
            {
              section: "STRIPE",
              title: "Stripe",
              icon: () => (
                <img
                  src="/assets/integrations/stripe-logo.svg"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.notion
        ? [
            {
              section: "NOTION",
              title: "Notion",
              icon: () => (
                <img
                  src="/assets/integrations/notion-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.asana
        ? [
            {
              section: "ASANA",
              title: "Asana",
              icon: () => (
                <img
                  src="/assets/integrations/asana-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.clickUp
        ? [
            {
              section: "CLICK_UP",
              title: "Click Up",
              icon: () => (
                <img
                  src="/assets/integrations/click-up-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.jira
        ? [
            {
              section: "JIRA",
              title: "Jira",
              icon: () => (
                <img
                  src="/assets/integrations/jira-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.github
        ? [
            {
              section: "GIT_HUB",
              title: "GitHub",
              icon: () => (
                <img
                  src="/assets/integrations/github-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      // CRMs
      ...(integrationConfigs?.hubSpot
        ? [
            {
              section: "HUB_SPOT",
              title: "Hubspot",
              icon: () => (
                <img
                  src="/assets/integrations/hubspot-logo.jpg"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.pipeDrive
        ? [
            {
              section: "PIPE_DRIVE",
              title: "PipeDrive",
              icon: () => (
                <img
                  src="/assets/integrations/pipe-drive-logo-2.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      // Calendar and SchedulingHelp Desks
      ...(integrationConfigs?.zenDesk
        ? [
            {
              section: "ZEN_DESK",
              title: "ZenDesk",
              icon: () => (
                <img
                  src="/assets/integrations/zen-desk-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.freshDesk
        ? [
            {
              section: "FRESH_DESK",
              title: "Fresh Desk",
              icon: () => (
                <img
                  src="/assets/integrations/fresh-desk-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(integrationConfigs?.zohoDesk
        ? [
            {
              section: "ZOHO_DESK",
              title: "Zoho Desk",
              icon: () => (
                <img
                  src="/assets/integrations/zoho-desk-logo.png"
                  style={{ width: "2rem" }}
                  alt=""
                  // className="scale-150 transform"
                />
              ),
            },
          ]
        : []),
      ...(isAitaEnabled
        ? [
            {
              section: "AITA",
              title: "Tara AI",
              icon: "ri-plane-line",
            },
          ]
        : []),
      ...(isTaDeskEnabled
        ? [
            {
              section: "TA_DESK",
              title: "TA Desk",
              icon: "ri-file-list-3-line",
            },
          ]
        : []),
      ...chatConnectorSidebars.map((item) => ({
        section: `CONNECTOR_${item.id}`,
        title: (item.data as iConnectorContactSideBarData).header.title,
        icon: ({ className }: { className?: string }) => (
          <IconRender
            icon={(item.data as iConnectorContactSideBarData).header.icon}
            className={className}
          />
        ),
      })),
    ],
    [
      chatConnectorSidebars,
      customProperties.length,
      integrationConfigs?.asana,
      integrationConfigs?.clickUp,
      integrationConfigs?.freshDesk,
      integrationConfigs?.github,
      integrationConfigs?.hubSpot,
      integrationConfigs?.jira,
      integrationConfigs?.notion,
      integrationConfigs?.pipeDrive,
      integrationConfigs?.stripe,
      integrationConfigs?.zenDesk,
      integrationConfigs?.zohoDesk,
      isAgentQCEnabled,
      isAitaEnabled,
      isTaDeskEnabled,
      mailchimpIntegrationConfig?.isActive,
      shopifyStoreConfig?.isActive,
      ticketTypes?.length,
    ],
  );

  return sectionHeadings;
};
